// Thin, framework-agnostic tracking helper.
// Safely no-ops when gtag / $crisp aren't loaded (dev, adblocker, headless bot).

const hasGtag = () => typeof window !== "undefined" && typeof window.gtag === "function";
const hasCrisp = () => typeof window !== "undefined" && Array.isArray(window.$crisp);

// Generic GA4 event
export const trackEvent = (name, params = {}) => {
  try {
    if (hasGtag()) window.gtag("event", name, params);
  } catch {
    // Analytics failures must never break the calling flow — GA4 can be
    // blocked by adblockers, corporate firewalls, or private-browsing modes.
    // Console-logging here would spam legitimate users; the call is a no-op.
  }
};

// Phone tap — fires GA4 "phone_click"
export const trackPhoneClick = (location = "unknown") => {
  trackEvent("phone_click", {
    event_category: "engagement",
    event_label: location,
    // Estimated inbound-call lead value (£) — tweak in one place later
    value: 25,
    currency: "GBP",
  });
};

// Portal sign-in click — fires GA4 "portal_click"
export const trackPortalClick = (location = "unknown") => {
  trackEvent("portal_click", {
    event_category: "portal",
    event_label: location,
  });
};

// Contact-form success — fires GA4 "generate_lead", sets Crisp user context
// (name/email/phone/service) so the operator sees who they're chatting to,
// pings a coloured session:event for inbox visibility, and shows a friendly
// "engineer is on it" auto-reply message in the visitor's chat window.
export const trackLeadGenerated = ({ service, company, name, email, phone } = {}) => {
  trackEvent("generate_lead", {
    event_category: "lead",
    event_label: service || "unspecified",
    value: 100,
    currency: "GBP",
  });

  try {
    if (!hasCrisp()) return;
    const c = window.$crisp;

    // 1. Attach visitor identity so the operator sees name/email/phone
    if (name) c.push(["set", "user:nickname", [name]]);
    if (email) c.push(["set", "user:email", [email]]);
    if (phone) c.push(["set", "user:phone", [phone]]);

    // 2. Store lead metadata on the session (visible in Crisp sidebar)
    const sessionData = [];
    if (service) sessionData.push(["service", service]);
    if (company) sessionData.push(["company", company]);
    sessionData.push(["source", "website_contact_form"]);
    if (sessionData.length) c.push(["set", "session:data", [sessionData]]);

    // 3. Green coloured event in the operator inbox for real-time visibility
    c.push([
      "set",
      "session:event",
      [[["lead_generated", { service: service || "unspecified", company: company || "", name: name || "" }, "green"]]],
    ]);

    // 4. Open the widget and post an auto-reply as if from an engineer
    const firstName = (name || "").split(/\s+/)[0] || "there";
    const msg = `Thanks ${firstName}! An engineer will call you back within the hour. If it's urgent right now, ring +44 7599 474730 or drop us a message here.`;
    c.push(["do", "chat:show"]);
    c.push(["do", "message:show", ["text", msg]]);
    c.push(["do", "chat:open"]);
  } catch {
    // Crisp SDK failures must never break the calling contact-form flow.
    // Adblockers commonly block Crisp; that's a graceful no-op, not an error
    // worth surfacing to the user or spamming the console for.
  }
};

